import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Edit component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconEdit extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M17.8016 2.60486C17.6936 2.60486 17.5867 2.62643 17.4871 2.66832C17.3876 2.7102 17.2974 2.77156 17.222 2.84877L17.2148 2.85609L4.49482 15.5142L2.94112 21.0631L8.49003 19.5094L21.1482 6.78951L21.1517 6.78605C21.2277 6.71069 21.288 6.62103 21.3291 6.52224C21.3703 6.42345 21.3915 6.31749 21.3915 6.21048C21.3915 6.10346 21.3703 5.9975 21.3291 5.89872C21.288 5.79993 21.2277 5.71027 21.1517 5.63491L21.148 5.63125L18.3812 2.84879C18.3057 2.77157 18.2155 2.7102 18.116 2.66832C18.0165 2.62643 17.9096 2.60486 17.8016 2.60486ZM16.9054 1.28574C17.1891 1.16636 17.4938 1.10486 17.8016 1.10486C18.1094 1.10486 18.4141 1.16636 18.6978 1.28574C18.9804 1.40467 19.2365 1.57864 19.4512 1.79752C19.452 1.79838 19.4529 1.79923 19.4537 1.80008L22.208 4.56991C22.2086 4.57055 22.2093 4.57119 22.2099 4.57183C22.4256 4.78624 22.5968 5.04112 22.7138 5.32185C22.8311 5.60343 22.8915 5.90544 22.8915 6.21048C22.8915 6.51551 22.8311 6.81753 22.7138 7.0991C22.5968 7.37985 22.4256 7.63474 22.2099 7.84916C22.2092 7.84979 22.2086 7.85042 22.208 7.85105L9.414 20.7075C9.32271 20.7992 9.20923 20.8657 9.0846 20.9006L2.06162 22.8671C1.8007 22.9401 1.52066 22.8668 1.32906 22.6752C1.13747 22.4836 1.06411 22.2036 1.13717 21.9426L3.10361 14.9196C3.1385 14.795 3.20505 14.6815 3.29679 14.5903L16.1534 1.79606C16.3678 1.57787 16.6234 1.4044 16.9054 1.28574Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-edit') === undefined) {
  customElements.define('cat-icon-edit', BlocksIconEdit);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-edit': BlocksIconEdit;
  }
}
